<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <ShareReport
              :report="report"
              :filter="baseFilter"
              @doShare="downloadReport"
              :dropdownItems="itemDownloadDropdown"
            />
            <DownloadReportDropdown :report="report" :items="itemDownloadDropdown" :actualTotal="actualTotal" />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">
        {{ $t('report.fields.dataSummary') }}
      </h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <AlertReportTooMany v-if="actualTotal > totalRows" />
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('address')"
      reportKey="raw-alert"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(createdOn)="data">
        {{ momentDateShortMonth(data.item.createdOn) }}
      </template>
      <template #cell(time)="data">
        {{ momentTimeSeconds(data.item.createdOn, true) }}
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle ? data.item.vehicle.licensePlate : '-' }}
      </template>

      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.manufacturer
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.model
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.type
              : '-'
            : '-'
        }}
      </template>

      <template #cell(formattedAlertData)="data">
        {{
          ![49, 51, 52, 53, 54, 55, 56, 90, 91, 92, 110, 111, 50, 113].includes(data.item.alertCode)
            ? data.item.formattedAlertData
            : '-'
        }}
      </template>

      <template #cell(alert)="data">
        {{ data.item.alert ? getAlertDesc(data.item.alert) : '-' }}
      </template>

      <template #cell(address)="data">
        <b-skeleton v-if="data.item.address == '' || data.item.address == null" width="70%" />
        <AddressValue
          v-else
          :id="`address-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.address"
        />
      </template>

      <template #cell(geofence)="data">
        {{ data.item.geofence }}
      </template>
      <template #cell(downloadVideo)="data">
        <McButton
          v-if="canDownloadVideo(data.item)"
          variant="secondary"
          :text="$t('report.fields.downloadVideo')"
          icon="icon-fill_download"
          size="sm"
          @click="handleDownload(data.item)"
        />
        <McButton
          v-else-if="isPendingVideo(data.item)"
          variant="secondary"
          :text="$t('global.action.prepareVideo')"
          icon="icon-fill_loader"
          size="sm"
          disabled
        />
        <McButton
          v-else-if="isFailedVideo(data.item)"
          variant="secondary"
          :text="$t('global.action.failedPrepare')"
          icon="icon-outline_close"
          size="sm"
          disabled
          :borderNone="false"
        />
      </template>

      <template #cell(vehicleGroup)="data">
        {{ getVehicleGroups(data.item.vehicle.vehicle2Vgroup) }}
      </template>

      <template #cell(falseAlert)="data">
        <span v-if="data.item.isFalseAlert" class="false-alert-icon">
          <McBadge text="False Alert" variant="danger" type="status" />
        </span>
        <span v-else-if="data.item.isResolved">
          <McBadge :text="$t('controlTower.detail.checked')" variant="primary" type="status" />
        </span>
        <span v-else>
          <McBadge :text="$t('controlTower.detail.notChecked')" variant="draft" type="status" />
        </span>
      </template>

      <template #cell(falseAlertReason)="data">
        {{ getFalseAlertReason(data.item.lastUserFeedback) }}
      </template>

      <template #cell(isImportant)="data">
        <span
          v-if="
            (data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read'])) ||
            data.item.isImportant
          "
          :class="
            data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read'])
              ? 'text-danger fw-bold'
              : 'text-warning fw-bold'
          "
          >{{
            data.item.isUrgent && hasAccess(['e4e-alert-center-urgent:read'])
              ? $t('report.object.urgent')
              : $t('report.object.important')
          }}</span
        >
        <span v-else>-</span>
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
    <ModalEmail
      v-if="actualTotal > totalRows"
      :id="idEmailModal"
      :recipients="recipientsEmail"
      @submit-emails="handleEmails"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { EXPORT_REPORT_ALERT, GET_REPORT_ALERT } from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import DownloadReportDropdown from '@/router/views/report/components/DownloadReportDropdown'
import ModalEmail from '@/router/views/report/components/ModalEmail'
import AlertReportTooMany from '../../components/AlertReportTooMany.vue'
import ShareReport from '../../components/ShareReport.vue'
import AddressValue from '../../components/AddressValue.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
    ModalEmail,
    AlertReportTooMany,
    ShareReport,
    AddressValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'alert',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
      alertsDMS: [49, 51, 52, 53, 54, 55, 56, 110, 111, 50],
      alertsADAS: [90, 91, 92, 113],
      emails: [],
      recipientsEmail: [],
      isSubmitEmail: false,
      idEmailModal: 'modal-email-alert',
      reportAfterEmail: '',
      actualTotal: 0,
      modalContactId: 'modal-contact',
    }
  },
  mounted() {
    const emailStorage = localStorage.getItem('emailReceiverReport')
    if (emailStorage) {
      this.recipientsEmail = emailStorage.split(',')
    }

    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
        .filter((item) => {
          if (item.key === 'downloadVideo') {
            return (
              this.filters.alertCode &&
              this.filters.alertCode.some(
                (code) =>
                  this.alertsDMS.includes(Number(code)) || this.alertsADAS.includes(Number(code))
              )
            )
          }
          return true
        })
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    baseFilter() {
      const filter = this.getBaseFilter(this.filters)
      let column = this.columns
      if (this.filters?.alertCode) {
        const allCodesNotInMap = this.filters.alertCode.every(
          (code) =>
            !this.alertsDMS.includes(Number(code)) && !this.alertsADAS.includes(Number(code))
        )

        if (allCodesNotInMap) {
          column = column.filter((value) => value !== 'download_video')
        }
      }
      const result = {
        ...filter,
        column,
      }
      return result
    },
    filterReport() {
      const { valid, model } = this.generateFilter
      if (!valid) return null
      const filterBody = {
        ...model,
      }

      return filterBody
    },
    generateFilter() {
      let { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (
        this.filters.vehicleId &&
        this.filters.vehicleId.length > 20 &&
        this.filters.alertCode &&
        this.filters.alertCode.length > 1
      ) {
        valid = false
        msg = this.$t('report.text.vehicleAlertSwal')
      }
      if (valid) {
        const model = this.baseFilter
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const filterReportFinal = { ...this.filterReport }
          if (!filterReportFinal) return {}
          const self = this
          self.isBusy = true
          filter.orderBy = JSON.stringify({ createdOn: 'asc' })
          const res = await this.queryCache.fetch(
            GET_REPORT_ALERT,
            {
              filter: {
                ...filterReportFinal,
                column: this.columns.filter((item) => item != 'address'),
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.reportAlert.reportAlert
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.reportAlert.message
          self.isError = !res.reportAlert.isSuccessful
          self.modalVerifReport += 1
          self.actualTotal = result?.pagination?.actualTotal ?? 0
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDatetimeShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDatetimeShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    itemDownloadDropdown() {
      let list = []
      if (this.getTotalDayReport(this.filters.periode) <= 7) {
        list = list.concat([
          {
            name: 'excel',
            label: this.$t('global.action.downloadExcel'),
            onClick: () => {
              this.downloadReport('xlsx')
            },
            icon: this.excelIcon,
          },
          {
            name: 'pdf',
            label: this.$t('global.action.downloadPdf'),
            onClick: () => {
              this.downloadReport('pdf')
            },
            icon: this.pdfIcon,
          },
        ])
      }
      if (this.hasCsvScope) {
        list.push({
          name: 'csv',
          label: this.$t('global.action.downloadCsv'),
          onClick: () => {
            this.downloadReport('csv')
          },
          icon: this.excelIcon,
        })
      }
      return list
    },
    hasEmail() {
      return this.emails && this.emails.length > 0
    },
  },
  methods: {
    getFalseAlertReason(feedback) {
      if (!feedback) return '-'
      return feedback.feedback ?? feedback.falseAlertReason ?? null
    },
    getBaseFilter(filters) {
      if (!filters || Object.keys(filters).length <= 0) return {}
      const result = {}
      Object.keys(filters).forEach((item) => {
        if (
          filters[item] !== null &&
          ((Array.isArray(filters[item]) && filters[item].length > 0) ||
            (!Array.isArray(filters[item]) && filters[item] != undefined))
        ) {
          result[item] = filters[item]
        }
      })
      const { periode, alertCode, falseAlert, isImportant, isUrgent, ...otherFilter } = result
      const startDate = periode.length > 0 ? moment(periode[0]).toISOString() : null
      const endDate = periode.length > 0 ? moment(periode[1]).toISOString() : null
      const alertCodes = alertCode.map((item) => item)
      /**
       * Adds alert code 95 to the `alertCode` array if alert code 94 is already present.
       * This is likely a special case handling for crash detection alert.
       */

      if (alertCodes.includes(94)) alertCodes.push(95)
      if (alertCodes.includes(1)) alertCodes.push(37)
      const model = {
        startDate,
        endDate,
        alertCode: alertCodes,
        isFalseAlert: falseAlert === null ? undefined : falseAlert,
        isImportant: !isImportant ? undefined : isImportant,
        isUrgent: !isUrgent ? undefined : isUrgent,
        ...otherFilter,
      }
      return model
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirectToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    handleEmails(emails) {
      this.emails = emails
      this.recipientsEmail = emails
      this.isSubmitEmail = true
      if (emails.length > 0) {
        localStorage.setItem('emailReceiverReport', emails.join())
      }
      this.$bvModal.hide(this.idEmailModal)
      this.$mcToast.info(this.$t('report.text.popupEmailInfo'))
      this.downloadReport(this.reportAfterEmail)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.alertReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension, contact) {
      if (
        !this.isSubmitEmail &&
        this.actualTotal > this.totalRows &&
        (!contact || contact.length <= 0)
      ) {
        this.$bvModal.show(this.idEmailModal)
        this.reportAfterEmail = extension
        return
      }
      this.isSubmitEmail = false

      const fileType = this.trackDownloadReport(this.report, extension, contact)

      const filterReport = this.filterReport
      if (!filterReport) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters

      let filterVar = {
        ...filterReport,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: fileType,
        emails: this.emails,
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }
      if (contact) {
        filterVar = {
          ...filterVar,
          isShared: true,
          contact: contact,
        }
      }

      this.$apollo
        .query({
          query: EXPORT_REPORT_ALERT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let result = res.data.alertReport
          if (!this.hasEmail && !contact) {
            this.showDownloader(periode, extension, result)
          } else if (contact) {
            if (result.isSuccessful) this.$mcToast.success(this.$t('report.text.sharedSuccess'))
            else this.$mcToast.error(this.$t('report.text.sharedFailed'))
          }
        })
    },
    handleDownloadVideo(urlVideo, alert, licensePlate, createdOn) {
      fetch(urlVideo)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const getLanguage = localStorage.getItem('language')
            ? localStorage.getItem('language')
            : 'id'
          const alertDescription =
            getLanguage === 'id' ? alert.descriptionIdn : alert.descriptionEng
          const customFileName = `${alertDescription} ${licensePlate}-CAM-${this.momentDatetimeShortMonthPlain(
            createdOn
          )}.mp4`
          link.setAttribute('download', customFileName)
          document.body.appendChild(link)
          link.click()
          link.parentNode.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
    },
    canDownloadVideo(item) {
      return (
        (this.alertsDMS.includes(item.alertCode) || this.alertsADAS.includes(item.alertCode)) &&
        this.getStatusVideo(item) === 'uploaded'
      )
    },
    isPendingVideo(item) {
      return (
        (this.alertsDMS.includes(item.alertCode) || this.alertsADAS.includes(item.alertCode)) &&
        this.getStatusVideo(item) === 'pending'
      )
    },
    isFailedVideo(item) {
      return (
        (this.alertsDMS.includes(item.alertCode) || this.alertsADAS.includes(item.alertCode)) &&
        this.getStatusVideo(item) === 'failed'
      )
    },
    handleDownload(item) {
      this.handleDownloadVideo(
        item.formattedAlertData,
        item.alert,
        item.vehicle.licensePlate,
        item.createdOn
      )
    },
  },
}
</script>
<style scoped>
.icon-error::before {
  color: red !important;
  font-size: 15px;
}
</style>
